import AttachFileIcon from '@mui/icons-material/AttachFile';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { type ReactNode } from 'react';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { modalStyles } from '@/components/Modal/Modal.styles';
import type { AddFilesResult } from '@/features/DataChangeRequest/_hooks/useAttachmentUpload';
import type { DcrAttachment } from '@/features/DataChangeRequest/_types/attachment';
import { AttachmentDropzone } from '@/features/DataChangeRequest/Attachments/AttachmentDropzone/AttachmentDropzone';
import { AttachmentGrid } from '@/features/DataChangeRequest/Attachments/AttachmentGrid/AttachmentGrid';
import { ComplianceConfirmation } from '@/features/DataChangeRequest/ReviewModalContent/ComplianceConfirmation/ComplianceConfirmation';
import { ReviewModalNotes } from '@/features/DataChangeRequest/ReviewModalContent/ReviewModalNotes/ReviewModalNotes';

import type { DcrReviewFormValues } from '../_hooks/useDcrReviewForm';

import { dcrReviewStyles as sx } from './DcrReviewFooter.styles';

type Props = {
	control: Control<DcrReviewFormValues>;
	attachments: DcrAttachment[];
	addFiles: (files: File[]) => AddFilesResult;
	removeAttachment: (id: string) => void;
	attachmentsExpanded: boolean;
	onExpandAttachments: () => void;
	actions: ReactNode;
};

export const DcrReviewFooter = ({
	control,
	attachments,
	addFiles,
	removeAttachment,
	attachmentsExpanded,
	onExpandAttachments,
	actions
}: Props) => {
	const { t } = useTranslation();

	return (
		<Box sx={sx.footerRoot}>
			<Box sx={sx.attachmentGroup}>
				<ReviewModalNotes control={control} />

				<Button
					variant='text'
					size='small'
					onClick={onExpandAttachments}
					startIcon={<AttachFileIcon fontSize='small' />}
					sx={sx.addAttachmentButton}
				>
					<Box component='span' data-slot='label'>
						{t('Add attachment')}
					</Box>
					{attachments.length > 0 && (
						<Box component='span' sx={sx.attachmentCount}>
							&nbsp;({attachments.length})
						</Box>
					)}
				</Button>

				{attachmentsExpanded && (
					<AttachmentDropzone attachments={attachments} addFiles={addFiles} />
				)}

				<AttachmentGrid
					attachments={attachments}
					removeAttachment={removeAttachment}
					size='large'
				/>
			</Box>

			<Divider sx={modalStyles.edgeDivider} />

			<ComplianceConfirmation control={control} />

			<Divider sx={modalStyles.edgeDivider} />

			{actions}
		</Box>
	);
};
